import moment, { type Moment } from "moment";
import { ExtensionDurationUnit, type ContractExtensionDto, type OptionDto } from "../../../autogen/bff-api";
import { lastDayOfPeriod } from "../../../common/contractPeriod";
import { isWholeExtensionNumber } from "../../../common/extension/extensionRules";
import { extendsContract, firstDeclined, isDecided, reachesInto } from "./extensionStatus";

export const withUnsavedPeriods = (
  options: OptionDto[],
  savedOptions: OptionDto[] | null | undefined,
  contractEnd: string | undefined
): OptionDto[] => {
  let currentEnd = contractEnd ? moment(contractEnd).startOf("day") : undefined;
  return options.map((option) => {
    const placed = savedOptions?.find((saved) => saved.id === option.id) ?? placedAfter(option, currentEnd);
    if (extendsContract(placed) && placed.endDate) currentEnd = moment(placed.endDate).startOf("day");
    return placed;
  });
};

const placedAfter = (option: OptionDto, currentEnd: Moment | undefined): OptionDto => {
  const extension = option.extension;
  if (!currentEnd || !extension || option.datesSetByHand) return option;
  const start = currentEnd.clone().add(1, "day");
  return {
    ...option,
    startDate: isoOrUndefined(start),
    endDate: isWholeExtensionNumber(extension.duration)
      ? isoOrUndefined(lastDayOfPeriod(start, months(extension)))
      : undefined,
    deadline: isWholeExtensionNumber(extension.exerciseDeadlineMonths)
      ? isoOrUndefined(currentEnd.clone().subtract(extension.exerciseDeadlineMonths, "months"))
      : undefined,
  };
};

// moment keeps isValid() true after arithmetic carries a date past JavaScript's last date, and toISOString then throws.
const isoOrUndefined = (date: Moment): string | undefined =>
  date.isValid() && Number.isFinite(date.valueOf()) ? date.toISOString() : undefined;

const months = (extension: ContractExtensionDto): number =>
  extension.unit === ExtensionDurationUnit.Years ? extension.duration * 12 : extension.duration;


export interface LapseAtEndDate {
  option: OptionDto;
  declined: OptionDto;
}

export const lapseAtEndDate = (options: OptionDto[], endDate: string | undefined): LapseAtEndDate | undefined => {
  const declined = firstDeclined(options);
  if (!declined || !endDate) return undefined;
  const option = withUnsavedPeriods(options, options.filter(isDecided), endDate).find(
    (placed) => !isDecided(placed) && reachesInto(placed, declined)
  );
  return option ? { option, declined } : undefined;
};
